/**
 * Wrapper for Born Modal.
 * Provides ease of usage for modals as well as some default fallbacks.
 */

import BornModal from '@borngroup/born-modal';
import {objectAssign} from '@borngroup/born-utilities';
import * as validation from './customValidation';
import {initModules, addEventListenerList} from '../helpers/utilities';
import * as progress from './progress';
import * as langEnablement from './langEnablement';
import modules from '../helpers/modules';

/**
 * @description populate the dialog with html content, then open it
 **/
function _openWithContent(params) {
    let content = params.content || params.html,
        modalInstance;

    if (content) {
        let originalCallback;

        params.options = objectAssign({}, params.options);
        params.tracking = objectAssign({}, params.tracking);
        params.options.content = content;

        originalCallback = params.callback || params.options.afterCreateCallback;

        params.options.afterCreateCallback = function(modal) {
            if (typeof originalCallback === 'function') {
                originalCallback(modal);
            }

            initModules(modal);
            validation.init();

            _handleTracking(modal, params);
        };

        modalInstance = new BornModal(params.options);

        delete params.options.afterCreateCallback;

        return modalInstance;
    }
}

/**
 * @description Replace the content of current dialog
 * @param {object} params
 * @param {string} params.url - If the url property is provided, an ajax call is performed to get the content to replace
 * @param {string} params.html - If no url property is provided, use html provided to replace
 */
export function open(params) {
    let targetModal;

    params.options = params.options || {};

    targetModal = BornModal.getModal(params.options.modalID);

    //Avoid making unnecessary requests if modal was already loaded once.
    if (targetModal) {
        //Makes sure the afterOpenCallback is taken from the object every time, in case the values change.
        targetModal.modal.afterOpenCallback = params.options.afterOpenCallback || function() {
            return false;
        };

        targetModal.modal.open();
    } else {
        if (params.url) {
            progress.showLoader();

            $.ajax({
                dataType: params.type || 'json',
                url: params.url,
                data: params.data,
                xhrFields: {
                    withCredentials: true
                }
            })
                .done(function(response) {
                    progress.hideLoader();

                    params.content = typeof response === 'string' ? response : (response.renderedTemplate || response.content);

                    _openWithContent(params);
                    if (window.SFRA.SitePreferences.languageExclusionList) {
                        langEnablement.langEnablementInit();
                    }
                })
                .fail(function(xhr, textStatus) {
                    // failed
                    if (textStatus === 'parsererror') {
                        console.log('Bad response type. Check if your `dataType` request matches the response type.');
                    }
                })
        } else if (params.html) {
            _openWithContent(params);
        } else if (params.selector) {
            params.content = document.querySelector(params.selector);
            _openWithContent(params);
        }
    }
}

export function replace(options) {
    var modal = options.modalID ? BornModal.getModal(options.modalID) : BornModal.getActiveModal();

    BornModal.updateModal(modal, options.content, options.newID);
}

/**
 * @function
 * @description Closes the dialog
 */
export function close() {
    BornModal.closeModal();
}

export function getActive() {
    return BornModal.getActiveModal();
}

/**
 * Returns the matched Modal element.
 * @param  {[type]} modalID [description]
 * @return {[type]}         [description]
 */
export function getModal(modalID) {
    return BornModal.getModal(modalID);
}

export function trigger(els, params) {
    addEventListenerList(els, 'click', function(evt) {
        evt.preventDefault();

        if (!params.url && !params.html && !params.selector) {
            params.selector = this.getAttribute('href');
        }

        open(params);
    });
}

function _handleTracking(modal, params) {
    let trackEvent = params.tracking.trackEvent || 'virtualPageview',
        trackObject = trackEvent === 'virtualPageview' ? {
            asyncURL: window.SFRA.Urls.GTMVirtualView,
            asyncParams: {
                nameOfPopin: params.tracking.modalName || params.options.modalID
            }
        } : params.tracking.trackObject;

    modules.tracking.init(null, {trackEvent, trackObject});
}
